import { ApiEndpoints, FacilityLicense } from 'models';
import { standardApi } from 'store/api';
import { StaffingGridItem } from 'pages/Anesthesia/components';
import { SortedColumns } from 'components';

import { ShortOption } from './FilterService';
import { AnesthesiaCoverageResponse, NurseCoverageResponse } from './CoreService';

import type { Facility, FieldMapping } from 'models/models';

const apiWithTags = standardApi.enhanceEndpoints({
	addTagTypes: ['Analytics'],
});

interface AnalyticsTableRequest {
	start_date: string;
	end_date: string;
	sort: SortedColumns[];
	healthsystem_id: number;
}

interface GraphAnalyticsRequest {
	start_date: string;
	end_date: string;
	group: string;
	healthsystem_id: number;
}

interface LoginTelemetry {
	days_since_last_login: number;
	interactions: number;
	number_of_unique_days: number;
	session_time: number;
	email: string;
	name: string;
	healthsystem_name: string;
}

interface PageTelemetry {
	page_url: string;
	interactions: number;
	visits: number;
	session_time: number;
}

interface GraphTelemetry {
	x: string;
	y: number;
}

/**
 * Provides endpoints for setting values in database
 */
export const analyticsService = apiWithTags.injectEndpoints({
	endpoints: (build) => ({
		getLoginAnalytics: build.query<{ stats: LoginTelemetry[] }, AnalyticsTableRequest>({
			query: (body) => ({
				url: ApiEndpoints.GET_LOGIN_EVENTS,
				method: 'POST',
				body,
			}),
			providesTags: ['Analytics'],
		}),
		getLoginGraph: build.query<{ data_points: GraphTelemetry[] }, GraphAnalyticsRequest>({
			query: (body) => ({
				url: ApiEndpoints.GET_LOGIN_EVENTS_GRAPH,
				method: 'POST',
				body,
			}),
			providesTags: ['Analytics'],
		}),
		getPageAnalytics: build.query<{ stats: PageTelemetry[] }, AnalyticsTableRequest>({
			query: (body) => ({
				url: ApiEndpoints.GET_PAGE_EVENTS,
				method: 'POST',
				body,
			}),
			providesTags: ['Analytics'],
		}),
		getPageGraph: build.query<{ data_points: GraphTelemetry[] }, GraphAnalyticsRequest>({
			query: (body) => ({
				url: ApiEndpoints.GET_PAGE_EVENTS_GRAPH,
				method: 'POST',
				body,
			}),
			providesTags: ['Analytics'],
		}),
		getUploadAnalytics: build.query<{ stats: PageTelemetry[] }, AnalyticsTableRequest>({
			query: (body) => ({
				url: ApiEndpoints.GET_UPLOAD_EVENTS,
				method: 'POST',
				body,
			}),
			providesTags: ['Analytics'],
		}),
		getUploadGraph: build.query<{ data_points: GraphTelemetry[] }, GraphAnalyticsRequest>({
			query: (body) => ({
				url: ApiEndpoints.GET_UPLOAD_EVENTS_GRAPH,
				method: 'POST',
				body,
			}),
			providesTags: ['Analytics'],
		}),
	}),
});

export const {
	useGetLoginAnalyticsQuery,
	useGetLoginGraphQuery,
	useGetPageAnalyticsQuery,
	useGetPageGraphQuery,
	useGetUploadAnalyticsQuery,
	useGetUploadGraphQuery,
} = analyticsService;

export const {
	endpoints: { getLoginAnalytics, getLoginGraph, getPageAnalytics, getPageGraph, getUploadGraph, getUploadAnalytics },
} = analyticsService;

// AI Usage / Token Tracking Types
export interface TokenUsageEntry {
	date: string;
	user?: string;
	model_name?: string;
	deployment_name?: string;
	input_tokens: number;
	output_tokens: number;
	total_tokens: number;
	request_count: number;
	healthsystem_name?: string;
	user_email?: string;
	cached_input_tokens?: number;
	// Detailed token breakdown
	cache_read_tokens: number;
	cache_creation_tokens: number;
	// Graph vs Resolver breakdown
	graph_input_tokens: number;
	graph_output_tokens: number;
	graph_cache_read_tokens: number;
	graph_cache_creation_tokens: number;
	graph_reasoning_tokens: number;
	// Resolver-specific tracking
	resolver_calls: number;
	resolver_input_tokens: number;
	resolver_output_tokens: number;
	resolver_cache_read_tokens: number;
	resolver_cache_creation_tokens: number;
	resolver_reasoning_tokens: number;
	// Cost
	billable?: number;
	input_cost?: number;
	cached_input_cost?: number;
	cache_write_cost?: number;
	output_cost?: number;
	total_cost?: number;
}

export interface AIUsageTableResponse {
	stats: TokenUsageEntry[];
	total_input_tokens: number;
	total_output_tokens: number;
	total_tokens: number;
	total_requests: number;
	// Additional totals for detailed breakdown
	total_cache_read_tokens: number;
	total_cache_creation_tokens: number;
	total_cache_write_cost?: number;
	total_graph_input_tokens: number;
	total_graph_output_tokens: number;
	total_resolver_calls: number;
	// Cost
	total_billable?: number;
}

export interface AIUsageGraphResponse {
	data_points: Array<{
		x: string;
		y?: number;
		y_in?: number;
		y_out?: number;
		input_tokens?: number;
		output_tokens?: number;
	}>;
	total_input_tokens: number;
	total_output_tokens: number;
	total_tokens: number;
	total_cache_read_tokens?: number;
}

interface AIUsageTableRequest {
	start_date: string;
	end_date: string;
	healthsystem_id: number;
	sort?: SortedColumns[];
}

interface AIUsageGraphRequest {
	start_date: string;
	end_date: string;
	group: string;
	healthsystem_id: number;
}

// AI Usage Service - extends the analytics service
export const aiUsageService = apiWithTags.injectEndpoints({
	endpoints: (build) => ({
		getAIUsageTable: build.query<AIUsageTableResponse, AIUsageTableRequest>({
			query: (body) => ({
				url: ApiEndpoints.GET_AI_USAGE_TABLE,
				method: 'POST',
				body,
			}),
			providesTags: ['Analytics'],
		}),

		getAIUsageGraph: build.query<AIUsageGraphResponse, AIUsageGraphRequest>({
			query: (body) => ({
				url: ApiEndpoints.GET_AI_USAGE_GRAPH,
				method: 'POST',
				body,
			}),
			providesTags: ['Analytics'],
		}),
	}),
});

export const { useGetAIUsageTableQuery, useGetAIUsageGraphQuery } = aiUsageService;

export const {
	endpoints: { getAIUsageTable, getAIUsageGraph },
} = aiUsageService;
